import { NodoDto, useGetNodoByQRCode, useGetNodoPortale } from "@/api";
import { useAuth } from "@/context/useAuth";
import Button from "@/elements/Button";
import { portaleConfig } from "@/portaliConfig";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Box, Breadcrumbs, Grid, Icon, Link, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import AlloggiamentiFiltroCard from "../CentraleDetailsPage/AlloggiamentiFiltroCard";
import AnalisiTableCard from "../CentraleDetailsPage/AnalisiTableCard";
import BonificheTableCard from "../CentraleDetailsPage/BonificheTable";
import CentraleOlioAttualeCard from "../CentraleDetailsPage/CentraleOlioAttualeCard";
import FiltrazioniTableCard from "../CentraleDetailsPage/FiltrazioniTable";
import GestisciReminderDialog from "../CentraleDetailsPage/GestisciReminderDialog";
import MacchinariSensoriTable from "../CentraleDetailsPage/MacchinariSensoriTable";
import NodoImmaginiCard from "../CentraleDetailsPage/NodoCard";
import DispositivoDetails from "../DispositivoDetails";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import NodoDialog from "../NodoDialog";
import QRCodeDialog from "../QRCodeDialog";
import ContenitoriTable from "./ContenitoriTable";
import ImmagineDinamicaContenitoreCard from "./ImmagineDinamicaContenitoreCard";
import moment from "moment";
import GatewayRipetitoriTable from "./GatewayRipetitoriTable";
import GatewayIcon from "@/assets/gateway.png";
import RipetitoreIcon from "@/assets/repeater.png";
import ConnessioniGatewayDialog from "../ConnessioniGatewayDialog";

export function Nodo({ nodo }: { nodo: NodoDto }) {
  // const aziende = useMemo(() => {
  //   return nodo.nodiFigli?.filter((x) => x.tipo === "Azienda") ?? [];
  // }, [nodo.nodiFigli]);

  // const hasAziende = aziende.length > 0;

  const { hasPermission, user } = useAuth();
  const { t } = useTranslation();

  const [isNodoDialogOpen, setIsNodoDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [qrcodeOpen, setQRCodeOpen] = useState(false);

  const breadcrumbLinks = useMemo(() => {
    const links: { title: string; link: string }[] = [];

    for (const crumb of nodo.breadcrumbs ?? []) {
      links.push({
        title: crumb.nodo.nome,
        link: `/nodi/${crumb.nodo.id}`,
      });
    }

    return links;
  }, [nodo.breadcrumbs]);

  const hasDispositivi = useMemo(() => {
    return nodo.nodiFigli?.some(
      (n) =>
        n.tipo === "Dispositivo_Sensore" || n.tipo === "Dispositivo_Macchinario"
    );
  }, [nodo.nodiFigli]);

  const hasGatewayOrRipetitori = useMemo(() => {
    return nodo.nodiFigli?.some(
      (n) => n.tipo === "Gateway" || n.tipo === "Ripetitore"
    );
  }, [nodo.nodiFigli]);

  const contenitoriFigli = useMemo(() => {
    return (nodo.nodiFigli ?? []).filter((n) => n.tipo === "Contenitore");
  }, [nodo.nodiFigli]);

  const handleOpenNodoDialog = () => {
    setIsNodoDialogOpen(true);
  };

  const handleCloseNodoDialog = () => {
    setIsNodoDialogOpen(false);
  };

  const trasferimento =
    nodo.breadcrumbs && nodo.breadcrumbs[nodo.breadcrumbs.length - 1];

  const hasReminders =
    nodo.tipo === "Contenitore" &&
    (nodo.haAnalisi || nodo.haAlloggiamentiFiltro || nodo.haStoricoBonifiche);

  const hasImmagineDinamica =
    nodo.tipo === "Contenitore" && nodo.haImmagineDinamica;

  const [connessioniGatewayDialogOpen, setConnessioniGatewayDialogOpen] =
    useState(false);

  return (
    <>
      <QRCodeDialog
        nodo={nodo}
        open={qrcodeOpen}
        onClose={() => {
          setQRCodeOpen(false);
        }}
      />
      <GestisciReminderDialog
        nodo={nodo}
        open={reminderDialogOpen}
        onClose={() => {
          setReminderDialogOpen(false);
        }}
      />
      <ConnessioniGatewayDialog
        open={connessioniGatewayDialogOpen}
        nodo={nodo}
        onClose={() => setConnessioniGatewayDialogOpen(false)}
        canEdit={hasPermission("ConnessioneGatewayChange")}
      />

      <Box>
        <Grid container>
          <Grid item p={1} xs={12} mb={2}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{ minHeight: "46px", justifyContent: "center" }}
            >
              <Breadcrumbs
                aria-label={t("nodoPage.breadcrumbAria")}
                separator={<Icon sx={{ mx: 0.5 }}>arrow_forward</Icon>}
                sx={{ my: 2 }}
              >
                {breadcrumbLinks.map(({ title, link }) => {
                  return (
                    <Link component={RouterLink} to={link}>
                      {title}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </Box>

            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box display="flex" flexDirection="column">
                <Box
                  display="flex"
                  flexDirection="row"
                  columnGap={3}
                  alignItems="center"
                >
                  {nodo.tipo === "Gateway" ? (
                    <img
                      src={GatewayIcon}
                      width={48}
                      style={{ paddingTop: 4 }}
                      alt={t("nodoPage.icone.gateway")}
                      title={t("nodoPage.icone.gateway")}
                    />
                  ) : nodo.tipo === "Ripetitore" ? (
                    <img
                      src={RipetitoreIcon}
                      width={48}
                      style={{ paddingTop: 4 }}
                      alt={t("nodoPage.icone.ripetitore")}
                      title={t("nodoPage.icone.ripetitore")}
                    />
                  ) : null}
                  <Typography variant="h4">{nodo.nome}</Typography>
                </Box>

                {trasferimento?.arco.da && (
                  <Typography variant="caption" fontWeight="medium">
                    {t("nodoPage.trasferimento.da", {
                      data: moment(trasferimento?.arco?.da).format("DD/MM/YYYY"),
                    })}
                  </Typography>
                )}
                {trasferimento?.arco.a && (
                  <Typography variant="caption" fontWeight="medium">
                    {t("nodoPage.trasferimento.a", {
                      data: moment(trasferimento?.arco?.a).format("DD/MM/YYYY"),
                    })}
                  </Typography>
                )}

                {trasferimento?.arco?.titolare && (
                  <Typography variant="caption" fontWeight="medium">
                    {t("nodoPage.trasferimento.proprietario")}: <b>{trasferimento.arco.titolare.nome}</b>
                  </Typography>
                )}

                {trasferimento?.arco.trasferimentoModalita && (
                  <Typography variant="caption" fontWeight="medium">
                    {t("nodoPage.trasferimento.modalita")}: <b>{trasferimento.arco.trasferimentoModalita}</b>
                  </Typography>
                )}
              </Box>

              <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
                {nodo.tipo === "Contenitore" && user?.isEsterno === false && (
                  <Box>
                    <Button
                      onClick={() => {
                        setQRCodeOpen(true);
                      }}
                      size="small"
                    >
                      {t("nodoPage.azioni.qrCode")}
                    </Button>
                  </Box>
                )}

                {nodo.tipo === "Ripetitore" && hasPermission("GatewayRead") && (
                  <Button
                    size="small"
                    variant="contained"
                    color="light"
                    onClick={() => setConnessioniGatewayDialogOpen(true)}
                  >
                    {t("nodoPage.azioni.gateway")}
                  </Button>
                )}

                {hasReminders &&
                  hasPermission("ContenitoreUpdateReminders") && (
                    <Box>
                      <Button
                        startIcon={<NotificationsIcon />}
                        onClick={() => {
                          setReminderDialogOpen(true);
                        }}
                        size="small"
                      >
                        {t("nodoPage.azioni.gestisciReminder")}
                      </Button>
                    </Box>
                  )}

                {((nodo.tipo === "Azienda" && hasPermission("AziendaEdit")) ||
                  (nodo.tipo === "Contenitore" &&
                    hasPermission("ContenitoreUpdate")) ||
                  ((nodo.tipo === "Dispositivo_Macchinario" ||
                    nodo.tipo === "Dispositivo_Sensore") &&
                    hasPermission("DispositivoUpdate"))) && (
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenNodoDialog}
                      size="small"
                    >
                      {t("azioni.modifica")}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          {(nodo.tipo === "Dispositivo_Macchinario" ||
            nodo.tipo === "Dispositivo_Sensore") && (
            <Grid item xs={12} p={1}>
              <DispositivoDetails nodo={nodo} />
            </Grid>
          )}

          {hasImmagineDinamica && (
            <Grid item xs={12} p={1}>
              <ImmagineDinamicaContenitoreCard nodo={nodo} />
            </Grid>
          )}

          {nodo.haSerbatoioOlio && (
            <Grid item xs={12} md={6} p={1}>
              <CentraleOlioAttualeCard
                nodo={nodo}
                olio={nodo.olioAttuale ?? null}
              />
            </Grid>
          )}

          {nodo.immagini && nodo.immagini.length > 0 && (
            <Grid item xs={12} md={6} p={1}>
              <NodoImmaginiCard nodo={nodo} immagini={nodo.immagini ?? []} />
            </Grid>
          )}

          {nodo.haAlloggiamentiFiltro && nodo.alloggiamentiFiltro && (
            <Grid item xs={12} md={6} p={1}>
              <AlloggiamentiFiltroCard
                nodo={nodo}
                alloggiamentiFiltro={nodo.alloggiamentiFiltro ?? []}
              />
            </Grid>
          )}

          {hasGatewayOrRipetitori && (
            <Grid item p={1} xs={12} mb={2}>
              <GatewayRipetitoriTable nodo={nodo} />
            </Grid>
          )}

          {(hasDispositivi || nodo.tipo === "Gateway") && (
            <Grid item mb={4} p={1} xs={12}>
              <MacchinariSensoriTable
                nodo={nodo}
                title={
                  nodo.tipo === "Gateway"
                    ? t("nodoPage.macchinariSensoriConnessi")
                    : undefined
                }
              />
            </Grid>
          )}

          {nodo.haAnalisi && (
            <Grid item p={1} xs={12} mb={4}>
              <AnalisiTableCard
                nodo={nodo}
                analisi={nodo.analisi || []}
                canEdit={hasPermission("ContenitoreUpdateAnalisi")}
              />
            </Grid>
          )}

          {nodo.haStoricoFiltrazioni && (
            <Grid item p={1} xs={12} mb={4}>
              <FiltrazioniTableCard
                nodo={nodo}
                filtrazioni={nodo.filtrazioni || []}
                canEdit={hasPermission("ContenitoreUpdateFiltrazioni")}
              />
            </Grid>
          )}

          {nodo.haStoricoBonifiche && (
            <Grid item p={1} xs={12}>
              <BonificheTableCard
                nodo={nodo}
                bonifiche={nodo.bonifiche || []}
                canEdit={hasPermission("ContenitoreUpdateBonifica")}
              />
            </Grid>
          )}

          {contenitoriFigli.length > 0 && (
            <Grid item p={1} xs={12} mb={2}>
              <ContenitoriTable nodo={nodo} />
            </Grid>
          )}
        </Grid>
      </Box>

      <NodoDialog
        open={isNodoDialogOpen}
        onClose={handleCloseNodoDialog}
        nodoId={nodo?.id}
      />
    </>
  );
}

export function NodoPage() {
  const { id } = useParams<{ id: string }>();
  const nodoId = parseInt(id || "0");

  const { data, error, isLoading } = useGetNodoPortale(
    portaleConfig.id,
    nodoId,
    {
      query: {
        refetchInterval: (data) =>
          data?.data.tipo === "Dispositivo_Macchinario" ||
          data?.data.tipo === "Dispositivo_Sensore" ||
          (data?.data.tipo === "Contenitore" && data?.data.haImmagineDinamica)
            ? 30_000
            : false,
      },
    }
  );

  if (error) {
    return <LoadingError error={error || undefined} />;
  }

  if (!data || isLoading) {
    return <Loading />;
  }

  return <Nodo nodo={data.data} />;
}

export function NodoDetailsPageQRCode() {
  const params = useParams();
  const token = params.token || "";
  const { t } = useTranslation();

  const { isLoading, error, data } = useGetNodoByQRCode(token);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return (
      <Box m={4}>
        <Typography color="secondary">
          {t("nodoPage.errori.qrCode")}
        </Typography>
      </Box>
    );
  }

  return <Nodo nodo={data.data} />;
}
